<template>
  <div class="container max-w-3xl mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold text-center mb-8 text-gray-800">Регистрация доктора в EMDEU</h1>
    
    <!-- Step progress bar -->
    <div class="flex items-center justify-between mb-10 max-w-xl mx-auto">
      <div v-for="(step, index) in steps" :key="index" class="flex flex-col items-center">
        <div 
          class="step-indicator" 
          :class="{
            'step-active': currentStep === index,
            'step-completed': currentStep > index,
            'step-inactive': currentStep < index
          }"
        >
          <span v-if="currentStep <= index">{{ index + 1 }}</span>
          <span v-else>✓</span>
        </div>
        <div class="text-sm mt-2 text-center">{{ step.label }}</div>
      </div>
    </div>
    
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <!-- Step 1: Personal Information -->
      <div v-if="currentStep === 0">
        <h2 class="text-xl font-semibold mb-6 text-center">Личная информация</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Фамилия *</label>
            <input 
              type="text" 
              v-model="formData.last_name"
              placeholder="Введите вашу фамилию"
              class="form-input"
              :class="{'border-red-500': errors.last_name}"
              @input="clearError('last_name')"
            >
            <p v-if="errors.last_name" class="mt-1 text-sm text-red-600">{{ errors.last_name }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Имя *</label>
            <input 
              type="text" 
              v-model="formData.first_name"
              placeholder="Введите ваше имя"
              class="form-input"
              :class="{'border-red-500': errors.first_name}"
              @input="clearError('first_name')"
            >
            <p v-if="errors.first_name" class="mt-1 text-sm text-red-600">{{ errors.first_name }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Отчество</label>
            <input 
              type="text" 
              v-model="formData.middle_name"
              placeholder="Введите ваше отчество (необязательно)"
              class="form-input"
              :class="{'border-red-500': errors.middle_name}"
              @input="clearError('middle_name')"
            >
            <p v-if="errors.middle_name" class="mt-1 text-sm text-red-600">{{ errors.middle_name }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">ИИН</label>
            <input 
              type="text" 
              v-model="formData.iin"
              placeholder="12-значный ИИН"
              class="form-input"
              maxlength="12"
              :class="{'border-red-500': errors.iin}"
              @input="clearError('iin')"
            >
            <p v-if="errors.iin" class="mt-1 text-sm text-red-600">{{ errors.iin }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Пол</label>
            <select 
              v-model="formData.gender"
              class="form-input"
              :class="{'border-red-500': errors.gender}"
              @change="clearError('gender')"
            >
              <option value="" disabled selected>Выберите пол</option>
              <option value="M">Мужской</option>
              <option value="F">Женский</option>
            </select>
            <p v-if="errors.gender" class="mt-1 text-sm text-red-600">{{ errors.gender }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Дата рождения</label>
            <input 
              type="date" 
              v-model="formData.birth_date"
              class="form-input"
              :class="{'border-red-500': errors.birth_date}"
              @input="clearError('birth_date')"
            >
            <p v-if="errors.birth_date" class="mt-1 text-sm text-red-600">{{ errors.birth_date }}</p>
          </div>
          
          <!-- Специфические поля для доктора -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Стаж работы (лет)</label>
            <input 
              type="number" 
              v-model="formData.experience_years"
              min="0"
              placeholder="Количество лет стажа"
              class="form-input"
              :class="{'border-red-500': errors.experience_years}"
              @input="clearError('experience_years')"
            >
            <p v-if="errors.experience_years" class="mt-1 text-sm text-red-600">{{ errors.experience_years }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input 
              type="email" 
              v-model="formData.email"
              placeholder="example@domain.com"
              class="form-input"
              :class="{'border-red-500': errors.email}"
              @input="clearError('email')"
            >
            <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Пароль</label>
            <input 
              type="password" 
              v-model="formData.password"
              placeholder="Минимум 8 символов"
              class="form-input"
              :class="{'border-red-500': errors.password}"
              @input="clearError('password')"
            >
            <p v-if="errors.password" class="mt-1 text-sm text-red-600">{{ errors.password }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Подтверждение пароля</label>
            <input 
              type="password" 
              v-model="formData.password_confirm"
              placeholder="Повторите пароль"
              class="form-input"
              :class="{'border-red-500': errors.password_confirm}"
              @input="clearError('password_confirm')"
            >
            <p v-if="errors.password_confirm" class="mt-1 text-sm text-red-600">{{ errors.password_confirm }}</p>
          </div>
        </div>

        <div v-if="submitError" class="bg-red-50 p-4 rounded-md mt-6 text-red-700">
          {{ submitError }}
        </div>
      </div>

      <!-- Step 2: City Selection -->
      <div v-if="currentStep === 1">
        <h2 class="text-xl font-semibold mb-6 text-center">Выберите город</h2>
        
        <div v-if="loading" class="flex justify-center p-6">
          <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
        
        <div v-else class="space-y-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Город</label>
          <select 
            v-model="formData.city"
            class="form-input"
            :class="{'border-red-500': errors.city}"
            @change="clearError('city')"
          >
            <option value="" disabled selected>Выберите город</option>
            <option v-for="city in cities" :key="city.id" :value="city.id">
              {{ city.name }}
            </option>
          </select>
          
          <p v-if="errors.city" class="mt-1 text-sm text-red-600">{{ errors.city }}</p>
        </div>

        <div v-if="submitError" class="bg-red-50 p-4 rounded-md mt-6 text-red-700">
          {{ submitError }}
        </div>
      </div>

      <!-- Step 3: Clinic Selection -->
      <div v-if="currentStep === 2">
        <h2 class="text-xl font-semibold mb-6 text-center">Выберите поликлинику</h2>
        
        <div v-if="loading" class="flex justify-center p-6">
          <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
        
        <div v-else-if="clinics.length === 0" class="text-center p-6">
          <p class="text-gray-500">В выбранном городе нет поликлиник</p>
          <button @click="prevStep" class="btn mt-4">Вернуться к выбору города</button>
        </div>
        
        <div v-else class="space-y-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Поликлиника</label>
          <select 
            v-model="formData.clinic"
            class="form-input"
            :class="{'border-red-500': errors.clinic}"
            @change="clearError('clinic')"
          >
            <option value="" disabled selected>Выберите поликлинику</option>
            <option v-for="clinic in clinics" :key="clinic.id" :value="clinic.id">
              {{ getLocalizedClinicName(clinic) }} ({{ clinic.address }})
            </option>
          </select>
          
          <p v-if="errors.clinic" class="mt-1 text-sm text-red-600">{{ errors.clinic }}</p>
        </div>

        <div v-if="submitError" class="bg-red-50 p-4 rounded-md mt-6 text-red-700">
          {{ submitError }}
        </div>
      </div>

      <!-- Submit step (Final confirmation) -->
      <div v-if="currentStep === 3">
        <h2 class="text-xl font-semibold mb-6 text-center">Подтверждение данных</h2>
        
        <div class="bg-gray-50 p-4 rounded-md mb-6">
          <dl class="space-y-3">
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Тип аккаунта:</dt>
              <dd class="text-sm text-gray-900 col-span-2">Врач</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">ФИО:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ formData.last_name }} {{ formData.first_name }} {{ formData.middle_name }}</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">ИИН:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ formData.iin }}</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Пол:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ formData.gender === 'M' ? 'Мужской' : 'Женский' }}</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Дата рождения:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ formData.birth_date }}</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Стаж работы:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ formData.experience_years }} лет</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Email:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ formData.email }}</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Город:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ selectedCityName }}</dd>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <dt class="text-sm font-medium text-gray-500">Поликлиника:</dt>
              <dd class="text-sm text-gray-900 col-span-2">{{ selectedClinicName }}</dd>
            </div>
            
            <!-- Удалено отображение участка -->
          </dl>
        </div>
        
        <div v-if="submitError" class="bg-red-50 p-4 rounded-md mt-6 text-red-700">
          {{ submitError }}
        </div>
      </div>
    </div>
    
    <!-- Navigation buttons -->
    <div class="flex justify-between mt-6">
      <button 
        v-if="currentStep > 0" 
        @click="prevStep" 
        class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
      >
        Назад
      </button>
      
      <div v-else class="w-24"></div>
      
      <div>
        <button 
          v-if="currentStep < 3" 
          @click="nextStep" 
          class="btn"
          :disabled="loading"
        >
          Далее
        </button>
        
        <button 
          v-else 
          @click="submitForm" 
          class="btn-secondary"
          :disabled="loading"
        >
          <span v-if="loading">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          Зарегистрироваться
        </button>
      </div>
    </div>
    
    <!-- Add login link and biometric registration option -->
    <div class="text-center mt-6">
      <p class="text-gray-600">
        Уже есть аккаунт? 
        <router-link to="/login" class="text-blue-600 hover:text-blue-800">
          Войти
        </router-link>
      </p>
      
      <!-- Biometric Registration Option (moved to bottom) -->
      <div class="mt-6 text-sm text-center bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p class="mb-2">Хотите попробовать как будет происходит биометрическая верификация для пациентов?</p>
        <router-link to="/biometric-register" class="inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-600">
          Регистрация через биометрию
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'RegisterForm',
  data() {
    return {
      currentStep: 0,
      loading: false,
      steps: [
        { label: 'Данные' },
        { label: 'Город' },
        { label: 'Поликлиника' },
        { label: 'Подтверждение' }
      ],
      formData: {
        role: 'doctor', // Автоматически устанавливаем роль "doctor"
        last_name: '',
        first_name: '',
        middle_name: '',
        iin: '',
        email: '',
        password: '',
        password_confirm: '',
        gender: '',
        birth_date: '',
        experience_years: '',
        city: null,
        clinic: null
      },
      cities: [],
      clinics: [],
      errors: {},
      submitError: '',
      successMessage: ''
    }
  },
  computed: {
    selectedCityName() {
      const city = this.cities.find(c => c.id === this.formData.city)
      return city ? city.name : ''
    },
    selectedClinicName() {
      const clinic = this.clinics.find(c => c.id === this.formData.clinic)
      return clinic ? this.getLocalizedClinicName(clinic) : ''
    },
    // selectedDistrictName удален
  },
  methods: {
    // Утилиты для получения локализованных названий
    getLocalizedClinicName(clinic) {
      if (this.$i18n.locale === 'kk' && clinic.name_kz) {
        return clinic.name_kz;
      }
      // Возвращаем русское название или обычное name как fallback
      return clinic.name || '';
    },

    // Navigation methods
    async nextStep() {
      if (this.currentStep === 0) {
        if (!this.validatePersonalInfo()) {
          return
        }

        // Загрузить города перед переходом к шагу выбора города
        await this.fetchCities()
      }
      else if (this.currentStep === 1) {
        if (!this.formData.city) {
          this.errors.city = 'Необходимо выбрать город'
          return
        }
        
        // Загрузить клиники для выбранного города
        await this.fetchClinics()
      }
      else if (this.currentStep === 2) {
        if (!this.formData.clinic) {
          this.errors.clinic = 'Необходимо выбрать поликлинику'
          return
        }
        
        // Удалено: больше не загружаем участки
      }
      
      this.currentStep += 1
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep -= 1
      }
    },
    
    // Form validation methods
    validatePersonalInfo() {
      let isValid = true
      this.errors = {}
      
      if (!this.formData.last_name) {
        this.errors.last_name = 'Фамилия обязательна для заполнения'
        isValid = false
      }
      
      if (!this.formData.first_name) {
        this.errors.first_name = 'Имя обязательно для заполнения'
        isValid = false
      }
      
      if (!this.formData.iin) {
        this.errors.iin = 'ИИН обязателен для заполнения'
        isValid = false
      } else if (!/^\d{12}$/.test(this.formData.iin)) {
        this.errors.iin = 'ИИН должен содержать 12 цифр'
        isValid = false
      }
      
      if (!this.formData.gender) {
        this.errors.gender = 'Пол обязателен для заполнения'
        isValid = false
      }
      
      if (!this.formData.birth_date) {
        this.errors.birth_date = 'Дата рождения обязательна для заполнения'
        isValid = false
      }
      
      // Проверка полей для доктора
      if (!this.formData.experience_years) {
        this.errors.experience_years = 'Стаж работы обязателен для заполнения'
        isValid = false
      } else if (isNaN(this.formData.experience_years) || this.formData.experience_years < 0) {
        this.errors.experience_years = 'Введите корректное количество лет'
        isValid = false
      }
      
      if (!this.formData.email) {
        this.errors.email = 'Email обязателен для заполнения'
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
        this.errors.email = 'Введите корректный email'
        isValid = false
      }
      
      if (!this.formData.password) {
        this.errors.password = 'Пароль обязателен для заполнения'
        isValid = false
      } else if (this.formData.password.length < 8) {
        this.errors.password = 'Пароль должен содержать минимум 8 символов'
        isValid = false
      }
      
      if (!this.formData.password_confirm) {
        this.errors.password_confirm = 'Подтверждение пароля обязательно'
        isValid = false
      } else if (this.formData.password !== this.formData.password_confirm) {
        this.errors.password_confirm = 'Пароли не совпадают'
        isValid = false
      }
      
      return isValid
    },
    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = null
      }
    },
    
    // API methods
    async fetchCities() {
      this.loading = true
      this.submitError = '' // Очистить предыдущие ошибки
      
      try {
        const response = await api.getCities()
        
        if (response.data && (response.data.results || Array.isArray(response.data))) {
          // Обработка как массива или объекта с полем results
          this.cities = response.data.results || response.data
        } else {
          this.submitError = 'Неправильный формат данных от сервера'
        }
      } catch (error) {
        
        if (error.response) {
          // Запрос был сделан, и сервер ответил кодом состояния, выходящим за пределы 2xx
          this.submitError = `Ошибка сервера: ${error.response.status} ${JSON.stringify(error.response.data)}`
        } else if (error.request) {
          // Запрос был сделан, но ответ не получен
          this.submitError = 'Нет ответа от сервера. Проверьте, запущен ли бэкенд.'
        } else {
          // Произошла ошибка при настройке запроса
          this.submitError = `Ошибка запроса: ${error.message}`
        }
      } finally {
        this.loading = false
      }
    },
    async fetchClinics() {
      this.loading = true
      try {
        const response = await api.getClinics(this.formData.city)
        this.clinics = response.data.results || response.data
      } catch (error) {
      // Error handled silently
      } finally {
        this.loading = false
      }
    },
    // fetchDistricts удален - больше не используется
    
    // Form submission
    validateLocationData() {
      let isValid = true;
      
      if (!this.formData.city) {
        this.errors.city = 'Необходимо выбрать город';
        isValid = false;
      }
      
      if (!this.formData.clinic) {
        this.errors.clinic = 'Необходимо выбрать поликлинику';
        isValid = false;
      }
      
      // Удалено: больше не требуется выбор участка
      
      return isValid;
    },
    
    async submitForm() {
      // Validate final step data
      if (!this.validateLocationData()) {
        return;
      }
      
      this.loadingSubmit = true;
      this.submitError = '';
      
      try {
        // Подготовка данных доктора
        let doctorData = {
          user: {
            email: this.formData.email,
            password: this.formData.password,
            password_confirm: this.formData.password_confirm,
            first_name: this.formData.first_name,
            last_name: this.formData.last_name,
            middle_name: this.formData.middle_name,
            iin: this.formData.iin
          },
          gender: this.formData.gender,
          specialization: 'Врач общей практики', // Значение по умолчанию
          experience_years: this.formData.experience_years,
          city: this.formData.city,
          clinic: this.formData.clinic,
          appointment_duration: 30 // Default appointment duration
        };
        
        // Регистрация доктора
        const response = await api.registerDoctor(doctorData);
        
        
        // Show success message
        this.successMessage = 'Регистрация успешно завершена!';
        
        // Redirect to login page after successful registration
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
        
      } catch (error) {
        this.submitError = error.response?.data?.detail || 'Ошибка при регистрации';
      } finally {
        this.loadingSubmit = false;
      }
    }
  },
  mounted() {
    // Fetch cities when component is mounted
    this.fetchCities()
  }
}
</script> 